import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw, XCircle, Loader2, FileStack, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SEC_LABEL = { cuentas: "Cuentas", cobranzas: "Cobranzas", pagares: "Pagarés" };

export default function InformesCargados({ onChanged }) {
  const [tab, setTab] = useState("cargas");
  const [cargas, setCargas] = useState([]);
  const [papelera, setPapelera] = useState([]);
  const [isSuper, setIsSuper] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {periodo,vendedor,seccion,label}
  const [general, setGeneral] = useState(null); // {periodo}
  const [generalTxt, setGeneralTxt] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/cartera/cargas"), api.get("/cartera/papelera")]);
      setCargas(c.data.cargas || []);
      setIsSuper(!!c.data.is_superadmin);
      setPapelera(p.data.papelera || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const afterChange = async () => { await load(); onChanged?.(); };

  const borrar = async (periodo, vendedor, seccion) => {
    setBusy(true);
    const tid = toast.loading("Borrando…");
    try {
      await api.post("/cartera/cargas/borrar", { periodo, vendedor, seccion: seccion || "todo" });
      toast.success("Movido a la papelera · restaurable 30 días", { id: tid });
      setConfirmDel(null);
      await afterChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo borrar", { id: tid });
    } finally { setBusy(false); }
  };

  const borrarGeneral = async () => {
    setBusy(true);
    const tid = toast.loading("Borrando período…");
    try {
      const res = await api.post("/cartera/cargas/borrar", { periodo: general.periodo, confirm: generalTxt });
      toast.success(`Borrados ${res.data.borrados} · omitidos ${res.data.omitidos}`, { id: tid });
      setGeneral(null); setGeneralTxt("");
      await afterChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo borrar", { id: tid });
    } finally { setBusy(false); }
  };

  const restaurar = async (id) => {
    setBusy(true);
    const tid = toast.loading("Restaurando…");
    try {
      await api.post(`/cartera/papelera/${id}/restaurar`);
      toast.success("Restaurado", { id: tid });
      await afterChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo restaurar", { id: tid });
    } finally { setBusy(false); }
  };

  const eliminarDef = async (id) => {
    if (!window.confirm("Eliminar DEFINITIVAMENTE. Esta acción no se puede deshacer.")) return;
    setBusy(true);
    const tid = toast.loading("Eliminando…");
    try {
      await api.delete(`/cartera/papelera/${id}`);
      toast.success("Eliminado definitivamente", { id: tid });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar", { id: tid });
    } finally { setBusy(false); }
  };

  const periodos = [...new Set(cargas.map((r) => r.periodo))].sort().reverse();

  return (
    <div data-testid="informes-cargados" className="space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button data-testid="tab-cargas" onClick={() => setTab("cargas")}
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "cargas" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`}>
          <FileStack className="inline h-4 w-4 mr-1" />Cargas
        </button>
        <button data-testid="tab-papelera" onClick={() => setTab("papelera")}
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "papelera" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`}>
          <Trash2 className="inline h-4 w-4 mr-1" />Papelera {papelera.length ? `(${papelera.length})` : ""}
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="inline h-5 w-5 animate-spin mr-2" />Cargando…</div>
      ) : tab === "cargas" ? (
        <>
          {periodos.length > 1 || periodos.length === 1 ? (
            <div className="flex flex-wrap gap-2">
              {periodos.map((p) => (
                <Button key={p} data-testid={`borrar-general-${p}`} size="sm" variant="outline"
                  onClick={() => { setGeneral({ periodo: p }); setGeneralTxt(""); }}
                  className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Borrar informe general {p}
                </Button>
              ))}
            </div>
          ) : null}

          {cargas.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No hay informes cargados.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                    <th className="text-left px-3 py-2.5 font-semibold">Período</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Vendedor</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Cuentas</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Cobranzas</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Pagarés</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Cargado por</th>
                    <th className="text-left px-3 py-2.5 font-semibold">Fecha</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cargas.map((r) => (
                    <tr key={`${r.periodo}-${r.vendedor}`} data-testid="carga-row" className="border-t border-slate-100 hover:bg-emerald-50/30">
                      <td className="px-3 py-2.5 font-mono text-slate-600">{r.periodo}</td>
                      <td className="px-3 py-2.5 font-medium text-slate-800">{r.vendedor}</td>
                      {["cuentas", "cobranzas", "pagares"].map((s) => (
                        <td key={s} className="px-3 py-2.5 text-center">
                          <div className="inline-flex items-center gap-1.5">
                            <span className="font-mono tabular-nums text-slate-700">{r.secciones?.[s] ?? 0}</span>
                            {r.puede_borrar && (r.secciones?.[s] ?? 0) > 0 && (
                              <button data-testid={`borrar-${s}-${r.vendedor}`} title={`Borrar ${SEC_LABEL[s]}`}
                                onClick={() => setConfirmDel({ periodo: r.periodo, vendedor: r.vendedor, seccion: s, label: SEC_LABEL[s] })}
                                className="text-slate-300 hover:text-red-600">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{r.created_by_name}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs">{(r.created_at || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2.5 text-right">
                        {r.puede_borrar && (
                          <Button size="sm" variant="outline" data-testid={`borrar-todo-${r.vendedor}`}
                            onClick={() => setConfirmDel({ periodo: r.periodo, vendedor: r.vendedor, seccion: "todo", label: "todas las secciones" })}
                            className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 gap-1">
                            <Trash2 className="h-3.5 w-3.5" /> Borrar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        papelera.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">La papelera está vacía.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                  <th className="text-left px-3 py-2.5 font-semibold">Período</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Vendedor</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Sección</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Borrado por</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Fecha</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {papelera.map((t) => (
                  <tr key={t.id} data-testid="papelera-row" className="border-t border-slate-100">
                    <td className="px-3 py-2.5 font-mono text-slate-600">{t.periodo}</td>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{t.vendedor}</td>
                    <td className="px-3 py-2.5 text-slate-600">{SEC_LABEL[t.seccion] || "Todas"}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">{t.deleted_by_name}</td>
                    <td className="px-3 py-2.5 text-slate-500 text-xs">{(t.deleted_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" data-testid={`restaurar-${t.id}`} disabled={busy}
                        onClick={() => restaurar(t.id)} className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 gap-1 mr-1.5">
                        <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                      </Button>
                      {isSuper && (
                        <Button size="sm" variant="outline" data-testid={`eliminar-${t.id}`} disabled={busy}
                          onClick={() => eliminarDef(t.id)} className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 gap-1">
                          <XCircle className="h-3.5 w-3.5" /> Eliminar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Confirmación de borrado individual / por sección / todo */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="confirm-borrar">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-center gap-2 text-red-600 font-semibold mb-2">
              <AlertTriangle className="h-5 w-5" /> Confirmar borrado
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Se borrará <b>{confirmDel.label}</b> de <b>{confirmDel.vendedor}</b> del período <b>{confirmDel.periodo}</b>. Podrás restaurarlo durante 30 días.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmDel(null)} disabled={busy}>Cancelar</Button>
              <Button size="sm" data-testid="confirm-borrar-ok" disabled={busy}
                onClick={() => borrar(confirmDel.periodo, confirmDel.vendedor, confirmDel.seccion)}
                className="bg-red-600 hover:bg-red-700 text-white gap-1">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Borrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Borrado general del período (escribir BORRAR) */}
      {general && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="confirm-general">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-center gap-2 text-red-600 font-semibold mb-2">
              <AlertTriangle className="h-5 w-5" /> Borrar informe general · {general.periodo}
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Se borrará TODO lo que tengas permitido del período <b>{general.periodo}</b> (los demás se omiten). Escribí <b>BORRAR</b> para confirmar.
            </p>
            <input data-testid="general-input" value={generalTxt} onChange={(e) => setGeneralTxt(e.target.value)}
              placeholder="BORRAR" className="w-full h-9 border border-slate-300 rounded px-3 text-sm mb-4 focus:outline-none focus:ring-1 focus:ring-red-500" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setGeneral(null)} disabled={busy}>Cancelar</Button>
              <Button size="sm" data-testid="general-ok" disabled={busy || generalTxt.trim().toUpperCase() !== "BORRAR"}
                onClick={borrarGeneral} className="bg-red-600 hover:bg-red-700 text-white gap-1">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Borrar período
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
